export type RetryOptions = {
    /** Maximum number of retries to attempt. Default: 5. */
    maxRetries: number;
    /** Initial delay in milliseconds before the first retry. Default: 1000. */
    initialDelayMs: number;
    /** Multiplier applied to the delay after each retry. Default: 2. */
    backoffMultiplier: number;
    /** Maximum delay in milliseconds between retries. Default: 15000. */
    maxDelayMs: number;
};

export const DEFAULT_RETRY_OPTIONS: RetryOptions = {
    maxRetries: 5,
    initialDelayMs: 1000,
    backoffMultiplier: 2,
    maxDelayMs: 15000,
};

export function calculateRetryDelay(attempt: number, options: RetryOptions): number {
    const delay = options.initialDelayMs * Math.pow(options.backoffMultiplier, attempt);
    return Math.min(delay, options.maxDelayMs);
}

/**
 * Retries an async operation with exponential backoff.
 *
 * @param operation - The async operation to retry.
 * @param options - Retry configuration options.
 * @param onRetry - Optional callback invoked before each retry with (attempt, error, delayMs).
 * @param onExhausted - Optional callback invoked when all retries are exhausted. Receives the last error.
 *                      Can throw a different error or return void to let the original error propagate.
 */
export async function retryWithBackoff<T>(
    operation: () => Promise<T>,
    options: RetryOptions = DEFAULT_RETRY_OPTIONS,
    onRetry?: (attempt: number, error: unknown, delayMs: number) => void,
    onExhausted?: (error: unknown) => void,
): Promise<T> {

    if (options.maxRetries < 0) {
        throw new Error('maxRetries must be a non-negative number');
    }

    if (options.maxRetries > 100) {
        throw new Error('maxRetries must be less than or equal to 100');
    }

    if (options.initialDelayMs < 0) {
        throw new Error('initialDelayMs must be a non-negative number');
    }

    if (options.backoffMultiplier < 1) {
        throw new Error('backoffMultiplier must be at least 1');
    }

    if (options.maxDelayMs < 0) {
        throw new Error('maxDelayMs must be a non-negative number');
    }

    let lastError: unknown;

    for (let attempt = 0; attempt <= options.maxRetries; attempt++) {
        try {
            return await operation();
        } catch (err) {
            lastError = err;

            if (attempt < options.maxRetries) {
                const delay = calculateRetryDelay(attempt, options);
                onRetry?.(attempt + 1, err, delay);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }

    // All retries exhausted
    onExhausted?.(lastError);
    throw lastError;
}
